import { Injectable } from '@angular/core';
import {
  DocketListResponse,
  DocumentListResponse,
  ImportDocumentRequest
} from '../../../types/api/dot-net-api-types/data-contracts';
import {environment} from '../../../../environments/environment';
import {documentEndpoints} from '../../../types/api/endpoints/document.endpoints';
import {HttpClient, HttpContext, HttpHeaders} from '@angular/common/http';
import {EMPTY, Observable} from 'rxjs';
import {BYPASS_TOKEN_INJECTION} from '../../../core/interceptors/fsx.interceptor.service';

/**
 * Interface for the Document API Service
 */
export interface IDocumentApiService {
  downloadDocument(documentId: string, renderingName: string): Observable<ArrayBuffer>;
  getDocumentUri(uri: string): void;
  uploadDocument(importDocumentRequest: ImportDocumentRequest, claimId?: string): void;
  getDocumentList(filingId: string): Observable<DocumentListResponse>;
  getDocketList(caseId: string): Observable<DocketListResponse>;
  stampDocument(filingId: string, documentId: string): void;
}

/**
 * Base url for the api.
 */
const BASE_URL = environment.ApiServer;

/**
 * Document Endpoints for the api.
 */
const DOCUMENT_ENDPOINTS = documentEndpoints(BASE_URL);

@Injectable({
  providedIn: 'root'
})
export class DocumentApiService implements IDocumentApiService {

  constructor(private readonly http: HttpClient) {}

  /**
   * Endpoint to download a document.
   * @param filingId
   * @param documentId
   * @param renderingName
   * ToDo: Need to figure out renderingName.
   */
  downloadDocument(filingId: string, documentId: string, renderingName?: string): Observable<ArrayBuffer> {
    const url = DOCUMENT_ENDPOINTS.downloadDocument(filingId, documentId, renderingName);
    return <Observable<ArrayBuffer>>this.http.get(url, {responseType: 'arraybuffer'});
  }

  /**
   * Endpoint to get a list of documents from a filing.
   * - If called anonymously and there is a secured document then the list will be empty and the flag will be set.
   * - If the user is logged in, but they aren't on the case and there's a secured document the list will be empty.
   * - If the user is logged in and there is a secure document, and they are on the case then it should return the list of documents.
   * @param filingId
   */
  getDocumentList(filingId: string): Observable<DocumentListResponse> {
    const url = DOCUMENT_ENDPOINTS.getDocumentList(filingId);
    return <Observable<DocumentListResponse>>this.http.get(url);
  }

  /**
   * Endpoint to get a document.
   * @param uri
   */
  getDocumentUri(): Observable<any> {
    const url = DOCUMENT_ENDPOINTS.getDocumentUri();
    return this.http.get(url);
  }

  /**
   * Endpoint to upload a document.
   * @param importDocumentRequest
   * @param claimId
   */
  uploadDocument(importDocumentRequest: ImportDocumentRequest, claimId?: string): Observable<Object> {
    const url = DOCUMENT_ENDPOINTS.postDocument();
    return <Observable<Object>>this.http.post(url, importDocumentRequest);
  }

  /**
   * Second endpoint to upload a document.
   * @param uri
   * @param file
   */
  uploadDocument2(uri: string, file: File): Observable<Object> {
    return this.http.put(uri, file, {
      context: new HttpContext().set(BYPASS_TOKEN_INJECTION, true),
      headers: new HttpHeaders({"x-ms-blob-type": "BlockBlob"})
    });
  }

  /**
   * Endpoint to get a list of dockets.
   * @param caseId
   */
  getDocketList(caseId: string): Observable<DocketListResponse> {
    const url = DOCUMENT_ENDPOINTS.getDocketList(caseId);
    return <Observable<DocketListResponse>>this.http.get(url);
  }

  /**
   * Endpoint to stamp a document.
   * @param filingId
   * @param documentId
   */
  stampDocument(filingId: string, documentId: string): Observable<Object> {
    const url = DOCUMENT_ENDPOINTS.stampDocument(filingId, documentId);
    return <Observable<Object>>this.http.post(url, {});
  }
}
